import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Workflow, Zap, CheckCircle, XCircle, Clock, TrendingUp, 
  DollarSign, Activity, BarChart3, ArrowUp, ArrowDown
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Activity className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const COLORS = ['#32B8C6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

  const statCards = [
    {
      label: "Total Workflows",
      value: analytics.workflows.total,
      icon: Workflow,
      color: "text-primary",
      bgColor: "bg-primary/10",
      change: null
    },
    {
      label: "Success Rate",
      value: `${analytics.executions.success_rate}%`,
      icon: CheckCircle,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      change: null
    },
    {
      label: "Hours Saved/Week",
      value: analytics.time_saved.hours_per_week,
      icon: Clock,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      suffix: "hrs"
    },
    {
      label: "Value/Month",
      value: `$${analytics.time_saved.value_per_month.toLocaleString()}`,
      icon: DollarSign,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      change: null
    }
  ];

  const executionData = analytics.executions_by_day.reverse().map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    executions: item.count
  }));

  const workflowStatusData = [
    { name: 'Published', value: analytics.workflows.published },
    { name: 'Draft', value: analytics.workflows.draft },
    { name: 'Scheduled', value: analytics.workflows.scheduled }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive insights into your automation performance
          </p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">{stat.label}</p>
                      <p className="text-3xl font-heading font-semibold text-foreground">
                        {stat.value}{stat.suffix && <span className="text-lg text-muted-foreground ml-1">{stat.suffix}</span>}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Row 1 */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Executions Over Time */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Workflow Executions (Last 30 Days)</CardTitle>
              <CardDescription>Daily execution volume</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={executionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#f3f4f6' }}
                  />
                  <Line type="monotone" dataKey="executions" stroke="#32B8C6" strokeWidth={2} dot={{ fill: '#32B8C6' }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Workflow Status Distribution */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Workflow Distribution</CardTitle>
              <CardDescription>Status breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={workflowStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {workflowStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Workflows */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Top Performing Workflows</CardTitle>
              <CardDescription>Most frequently executed</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.top_workflows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#f3f4f6' }}
                  />
                  <Bar dataKey="executions" fill="#32B8C6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Workflow Performance */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Workflow Success Rates</CardTitle>
              <CardDescription>Performance by workflow</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.workflow_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#6b7280" fontSize={12} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#f3f4f6' }}
                  />
                  <Bar dataKey="success_rate" fill="#22c55e" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Time & Cost Savings */}
        <Card className="glass-card border-primary/30">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Time & Cost Savings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl font-heading font-semibold text-primary mb-2">
                  {analytics.time_saved.hours_per_week}
                </div>
                <p className="text-sm text-muted-foreground">Hours Saved/Week</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-heading font-semibold text-primary mb-2">
                  {analytics.time_saved.hours_per_month}
                </div>
                <p className="text-sm text-muted-foreground">Hours Saved/Month</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-heading font-semibold text-green-500 mb-2">
                  ${analytics.time_saved.value_per_week.toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">Value/Week</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-heading font-semibold text-green-500 mb-2">
                  ${analytics.time_saved.value_per_month.toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">Value/Month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading">Recent Workflow Executions</CardTitle>
            <CardDescription>Last 10 workflow runs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.recent_executions.slice(0, 10).map((exec) => (
                <div key={exec.execution_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    {exec.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : exec.status === 'failed' ? (
                      <XCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <Activity className="w-5 h-5 text-amber-500 animate-spin" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">{exec.workflow_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(exec.started_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge variant={exec.status === 'completed' ? 'default' : 'destructive'}>
                    {exec.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
